import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  signal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { Subject, of, switchMap } from 'rxjs';
import { createActionState } from '@at/shared';
import { Trip } from '@at/trip';
import { CreateTripLinkResponse, CancelTripLinkInviteResponse } from '../../api';
import { InvitesApi } from '../../api/invites.api';

@Component({
  selector: 'at-link-invite',
  standalone: true,
  imports: [NgTemplateOutlet],
  templateUrl: './link-invite.component.html',
  styleUrl: './link-invite.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LinkInviteComponent {
  readonly #tripInvitesApi = inject(InvitesApi);

  public dialog = input<HTMLDialogElement>();
  public trip = model.required<Trip>();

  protected readonly linkText = computed(() => {
    const trip = this.trip();
    if (!trip.inviteUrl) return '';

    return trip.description
      ? `You're invited!\n\nJoin AnotherTrip and mark your availability for ${trip.title}: ${trip.description}.\n\n${trip.inviteUrl}`
      : `You're invited!\n\nJoin AnotherTrip and mark your availability for ${trip.title}.\n\n${trip.inviteUrl}`;
  });

  protected readonly action = new Subject<'create' | 'disable'>();

  protected readonly state = toSignal(
    createActionState({
      trigger: this.action,
      apiCall: (action: string) =>
        of(action).pipe(
          switchMap((action) => {
            return action === 'create'
              ? this.#tripInvitesApi.createLinkInvite({ tripId: this.trip().tripId })
              : this.#tripInvitesApi.deleteLinkInvite(this.trip().inviteUrl);
          }),
        ),
      onSucceed: (response: CreateTripLinkResponse | CancelTripLinkInviteResponse) => {
        if ('inviteUrl' in response) {
          this.trip.update((trip) => ({ ...trip, inviteUrl: response.inviteUrl }));
          this.copyLink();
        } else this.trip.update((trip) => ({ ...trip, inviteUrl: undefined }));
      },
    }),
  );

  protected copied = signal(false);

  protected async copyLink() {
    const link = this.trip().inviteUrl;
    if (!link) return;

    const joinPhrase = `Join ${this.trip().title} in AnotherTrip`;
    const linkTextData = new Blob([link], { type: 'text/plain' });
    const linkHtmlData = new Blob([`<a href="${link}">${joinPhrase}</a>`], { type: 'text/html' });

    await navigator.clipboard.write([
      new ClipboardItem({ 'text/plain': linkTextData, 'text/html': linkHtmlData }),
    ]);

    this.copied.set(true);
  }
  protected readonly isNativeShareSupported = Boolean(navigator.share);
  protected async share(title: string, text: string, url: string) {
    if (navigator.share) {
      await navigator.share({
        title: title,
        url: url,
      });
    } else {
      console.error('Trying to share and native web share not supported');
    }
  }

  constructor() {
    effect(() => {
      if (this.copied()) {
        setTimeout(() => this.copied.set(false), 3000);
      }
    });
  }
}
